import React, { useState, useEffect, useRef } from 'react';

import { FIELD_NAME_MAP, DEFAULT_TRIMS, getInputValue, formatNumericValue, parseInputValue } from '../utils';

import DisplayCost from './DisplayCost';

const TrimsSection = ({
	data = [],
	totalCost = 0,
	onChange,
	trimsAdditionalCharge = '',
	onTrimsAdditionalChargeChange,
	isEditable = true
}) => {
	const [trims, setTrims] = useState({ ...DEFAULT_TRIMS });
	const [originalTrimsData, setOriginalTrimsData] = useState([]);
	const [additionalCharge, setAdditionalCharge] = useState(trimsAdditionalCharge);
	const isInitialMount = useRef(true);
	const prevDataRef = useRef(null);
	const prevAdditionalChargeRef = useRef(null);

	useEffect(() => {
		const dataString = JSON.stringify(data);
		const prevDataString = JSON.stringify(prevDataRef.current);

		if (dataString === prevDataString) {
			return;
		}

		const trimsObj = { ...DEFAULT_TRIMS };

		if (data && data.length > 0) {
			data.forEach((trim) => {
				if (trim.field_name && FIELD_NAME_MAP[trim.field_name]) {
					const internalFieldName = FIELD_NAME_MAP[trim.field_name];

					trimsObj[internalFieldName] = formatNumericValue(trim.total_price);
				}
			});
		}

		setTrims(trimsObj);
		setOriginalTrimsData(data && data.length > 0 ? [...data] : []);

		prevDataRef.current = data;
		if (isInitialMount.current) {
			isInitialMount.current = false;
		}
	}, [data]);

	useEffect(() => {
		if (prevAdditionalChargeRef.current !== trimsAdditionalCharge) {
			setAdditionalCharge(
				trimsAdditionalCharge !== undefined && trimsAdditionalCharge !== null
					? trimsAdditionalCharge
					: ''
			);
			prevAdditionalChargeRef.current = trimsAdditionalCharge;
		}
	}, [trimsAdditionalCharge]);

	const transformToApiFormat = (updatedTrims) => {
		const trimsData = [];
		const updatedFields = new Set();

		if (originalTrimsData.length > 0) {
			originalTrimsData.forEach((trim) => {
				if (trim.field_name && FIELD_NAME_MAP[trim.field_name]) {
					const internalFieldName = FIELD_NAME_MAP[trim.field_name];
					const updatedValue = updatedTrims[internalFieldName];
					updatedFields.add(internalFieldName);

					const formattedTotalPrice = formatNumericValue(updatedValue);

					trimsData.push({
						...trim,

						trims_price_usd: trim.trims_price_usd || '',

						total_price:
							formattedTotalPrice !== '' &&
							formattedTotalPrice !== null &&
							formattedTotalPrice !== undefined
								? formattedTotalPrice.toString()
								: trim.total_price || '0'
					});
				}
			});
		}

		Object.keys(updatedTrims).forEach((fieldName) => {
			if (!updatedFields.has(fieldName)) {
				const value = updatedTrims[fieldName];

				if (value !== '' && value !== null && value !== undefined) {
					const apiFieldName = Object.keys(FIELD_NAME_MAP).find(
						(key) => FIELD_NAME_MAP[key] === fieldName
					);
					if (apiFieldName) {
						const formattedTotalPrice = formatNumericValue(value);
						trimsData.push({
							trim_name: fieldName,
							trims_price_usd: '',
							quantity: '',
							total_price:
								formattedTotalPrice !== '' &&
								formattedTotalPrice !== null &&
								formattedTotalPrice !== undefined
									? formattedTotalPrice.toString()
									: '0',
							field_name: apiFieldName
						});
					}
				}
			}
		});

		return trimsData;
	};

	const notifyChange = (updatedTrims) => {
		if (onChange && !isInitialMount.current) {
			onChange(transformToApiFormat(updatedTrims));
		}
	};

	const handleInputChange = (field, value) => {
		const updatedValue = parseInputValue(value);
		const updatedTrims = { ...trims, [field]: updatedValue };
		setTrims(updatedTrims);
		notifyChange(updatedTrims);
	};

	const handleAdditionalChargeChange = (value) => {
		const updatedValue = parseInputValue(value);
		setAdditionalCharge(updatedValue);
		if (onTrimsAdditionalChargeChange && !isInitialMount.current) {
			onTrimsAdditionalChargeChange(formatNumericValue(updatedValue));
		}
	};

	return (
		<div className="single-table trims-content mt-5">
			<div className="d-flex justify-content-between mb-2">
				<p>Trims Cost Metrics</p>
			</div>
			<div className="row">
				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Sewing Thread</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.sewingThread)}
						onChange={(e) => handleInputChange('sewingThread', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Main Label</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.mainLabel)}
						onChange={(e) => handleInputChange('mainLabel', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Care Label</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.careLabel)}
						onChange={(e) => handleInputChange('careLabel', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>
				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Size Label</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.sizeLabel)}
						onChange={(e) => handleInputChange('sizeLabel', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>
				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Zipper</p>
					<input
						type="number"
						step="0.01"
						value={trims.zipper}
						onChange={(e) => handleInputChange('zipper', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Tape-Design/Lace</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.tapeDesignLace)}
						onChange={(e) => handleInputChange('tapeDesignLace', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Button</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.button)}
						onChange={(e) => handleInputChange('button', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Shank Button</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.shankButton)}
						onChange={(e) => handleInputChange('shankButton', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Snap Button</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.snapButton)}
						onChange={(e) => handleInputChange('snapButton', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Metal Hook & Ring</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.metalHookRing)}
						onChange={(e) => handleInputChange('metalHookRing', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Stopper/Pullar</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.stopperPullar)}
						onChange={(e) => handleInputChange('stopperPullar', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Nipple Up Rivet</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.nippleUpRivet)}
						onChange={(e) => handleInputChange('nippleUpRivet', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Buckle</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.buckle)}
						onChange={(e) => handleInputChange('buckle', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Cord</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.cord)}
						onChange={(e) => handleInputChange('cord', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Draw Cord</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.drawCord)}
						onChange={(e) => handleInputChange('drawCord', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Cord/Wire with Metal Stopper</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.cordWireMetalStopper)}
						onChange={(e) => handleInputChange('cordWireMetalStopper', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Elastics</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.elastics)}
						onChange={(e) => handleInputChange('elastics', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Fusing</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.fusing)}
						onChange={(e) => handleInputChange('fusing', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Others</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(trims.others)}
						onChange={(e) => handleInputChange('others', e.target.value)}
						readOnly={!isEditable}
					/>
				</div>

				<div className="col-md-6 col-sm-12 mb-2">
					<p className="regular-12 gray_dark_02 pb-1">Trims Additional Charge (%)</p>
					<input
						type="number"
						step="0.01"
						value={getInputValue(additionalCharge)}
						onChange={(e) => handleAdditionalChargeChange(e.target.value)}
						readOnly={!isEditable}
					/>
				</div>
			</div>
			<div className="row align-items-center m-0 total-row">
				<div className="col-md-6 col-sm-12 mb-2 mb-0">
					<p className="semibold-14 gray_dark_01">Total Trims Cost</p>
				</div>
				<div className="col-md-6 col-sm-12 mb-2 mb-0">
					<p className="semibold-14 gray_dark_01 float-right">
						<DisplayCost totalCost={totalCost} />
					</p>
				</div>
			</div>
		</div>
	);
};

export default TrimsSection;
